import React from "react";
import {
  Typography,
  Stack,
  LinearProgress,
  Box,
  Fade,
  Slide,
} from "@mui/material";
import { styled, alpha, keyframes } from "@mui/material/styles";
import Conversations from "./Conversations";
import FilteredSearch from "./FilteredSearch";
import useApp from "../../../store/contexts/AppContext";

// Keyframe animations
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Styled components
const LoadingContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  padding: "20px",
  gap: "12px",
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  backgroundColor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.08),
  "& .MuiLinearProgress-bar": {
    borderRadius: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light || theme.palette.primary.main})`,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "80px 20px",
  animation: `${pulse} 2s ease-in-out infinite`,
}));

const EmptyIcon = styled(Box)(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.15
  )}, ${alpha(theme.palette.primary.dark, 0.05)})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "24px",
  fontSize: "3rem",
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.7)
    : alpha(theme.palette.text.primary, 0.7),
  fontSize: "1.1rem",
  fontWeight: 600,
  textAlign: "center",
  marginBottom: "8px",
}));

const EmptySubtext = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.4)
    : alpha(theme.palette.text.secondary, 0.5),
  fontSize: "0.9rem",
  textAlign: "center",
}));

const ChatListsContainer = styled(Box)({
  animation: `${slideIn} 0.5s ease-out`,
  position: "relative",
  zIndex: 1,
});

export default function ChatLists(props) {
  // Accept props for backward compatibility but don't use them
  const {
    appState: { chats, chatLoading, filteredChats, searchInProgress },
  } = useApp();

  // Loading state with animated progress bars
  if (searchInProgress) {
    return (
      <Fade in timeout={400}>
        <LoadingContainer>
          <StyledLinearProgress variant="indeterminate" />
          <StyledLinearProgress
            variant="indeterminate"
            sx={{
              animationDelay: "0.2s",
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(90deg, ${alpha(
                  "#fff",
                  0.3
                )}, ${alpha("#fff", 0.1)})`,
              },
            }}
          />
          <StyledLinearProgress
            variant="indeterminate"
            sx={{
              animationDelay: "0.4s",
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(90deg, ${alpha(
                  "#fff",
                  0.2
                )}, ${alpha("#fff", 0.05)})`,
              },
            }}
          />
        </LoadingContainer>
      </Fade>
    );
  }

  // Filtered search results
  if (filteredChats?.success) {
    return (
      <Slide direction="right" in timeout={500}>
        <ChatListsContainer>
          <FilteredSearch styles={props?.styles} />
        </ChatListsContainer>
      </Slide>
    );
  }

  // Main chat list
  return (
    <ChatListsContainer>
      {chats?.length ? (
        <Conversations />
      ) : chatLoading ? (
        <Fade in timeout={600}>
          <EmptyState>
            <EmptyIcon>💬</EmptyIcon>
            <EmptyText>Loading conversations...</EmptyText>
            <EmptySubtext>Please wait a moment</EmptySubtext>
          </EmptyState>
        </Fade>
      ) : (
        <Fade in timeout={600}>
          <EmptyState>
            <EmptyIcon>📭</EmptyIcon>
            <EmptyText>No messages yet</EmptyText>
            <EmptySubtext>Start a new conversation to get started</EmptySubtext>
          </EmptyState>
        </Fade>
      )}
    </ChatListsContainer>
  );
}
